import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import Globe from './components/Globe';
import CountryAdventureModal from './components/CountryAdventureModal';
import LandmarkPanel from './components/LandmarkPanel';
import MascotGuide from './components/MascotGuide';
import DiscoveryEffect from './components/DiscoveryEffect';
import BottomNav from './components/BottomNav';
import StickerBook from './components/StickerBook';
import QuestList, { QUESTS } from './components/QuestList';
import ConsentBanner from './components/ConsentBanner';
import SoundToggle from './components/SoundToggle';
import { trackEvent } from './firebase';
import { warmUp } from './utils/soundEngine';
import useMediaQuery from './utils/useMediaQuery';
import { logClientError } from './utils/errorLogger';
import { getKidFriendlyError } from './utils/kidFriendlyErrors';

import { API } from './config';
const DISCOVERIES_KEY = 'orboot.discoveries.v1';

class GlobeErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) {
    console.error('[Globe crash]', err, info);
    logClientError(err, {
      type: 'react-error-boundary',
      componentStack: info?.componentStack,
    });
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={s.errorScreen}>
        <div style={s.errorEmoji}>🌍</div>
        <h2 style={s.errorTitle}>Oops! The globe got dizzy.</h2>
        <p style={s.errorBody}>Try reloading the page.</p>
        <button onClick={() => window.location.reload()} style={s.errorBtn}>Try again</button>
      </div>
    );
  }
}

export default function App() {
  const isMobile = useMediaQuery('(max-width: 640px)');
  // Globe state
  const [selected, setSelected]                       = useState(null);
  const [loading, setLoading]                         = useState(false);
  const [loadError, setLoadError]                     = useState(false);
  const [loadErrorMessage, setLoadErrorMessage]       = useState('');
  const [zoomed, setZoomed]                           = useState(false);
  const [zoomTarget, setZoomTarget]                   = useState(null);
  const [selectedCountryName, setSelectedCountryName] = useState(null);
  const [selectedLandmark, setSelectedLandmark]       = useState(null);
  // Monotonic id so a stale arrival-delay can't open the modal for a country
  // the kid has already navigated away from.
  const countryRequestRef = useRef(0);

  // Navigation
  const [activeTab, setActiveTab] = useState('globe');

  // Search
  const [allCountries, setAllCountries]   = useState([]);
  const [searchQuery, setSearchQuery]     = useState('');
  const [searchTrigger, setSearchTrigger] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    fetch('/countries.json').then(r => r.json()).then(setAllCountries).catch(() => {});
  }, []);

  const suggestions = searchQuery.length > 1
    ? allCountries.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 7)
    : [];

  const handleSearchSelect = useCallback((country) => {
    setSearchQuery('');
    setSearchTrigger({ ...country, _ts: Date.now() });
    trackEvent('search_select', { country_name: country.name });
  }, []);

  // Country / landmark handlers
  const handleCountryClick = useCallback(async (code, name, point) => {
    if (loading) return;
    setActiveTab('globe');
    setZoomed(true);
    setZoomTarget(point);
    setSelectedCountryName(name);
    setLoading(true);
    setLoadError(false);
    setLoadErrorMessage('');
    setSelected(null);
    setSelectedLandmark(null);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 30_000);
    const startedAt = Date.now();
    const requestId = ++countryRequestRef.current;
    try {
      const res = await fetch(`${API}/api/country/${code}?name=${encodeURIComponent(name)}`, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      // Arrival moment: data is ready, so drop the loading card and let the
      // zoom land on the country diorama for a beat before the fullscreen
      // modal covers the globe. Cached countries resolve instantly, which
      // used to slam the modal over the animation.
      setLoading(false);
      const ARRIVAL_MS = 2500;
      const remaining = ARRIVAL_MS - (Date.now() - startedAt);
      if (remaining > 0) await new Promise((r) => setTimeout(r, remaining));
      if (countryRequestRef.current !== requestId) return; // superseded
      setSelected(json.data);
      trackEvent('country_loaded', { country_code: code, country_name: name });
    } catch (e) {
      console.error('Failed to load country:', e);
      logClientError(e, {
        type: 'country-load',
        details: { code, name },
      });
      setLoadError(true);
      setLoadErrorMessage(getKidFriendlyError('country'));
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  }, [loading]);

  const handleLandmarkClick = useCallback((name) => {
    setSelected(null);
    setLoadError(false);
    setLoadErrorMessage('');
    setLoading(false);
    setZoomed(false);
    setZoomTarget(null);
    setSelectedCountryName(null);
    setSelectedLandmark(name);
    trackEvent('landmark_opened', { landmark: name });
  }, []);

  const handleClose = useCallback(() => {
    setSelected(null);
    setLoadError(false);
    setLoadErrorMessage('');
    setZoomed(false);
    setZoomTarget(null);
    setSelectedCountryName(null);
    setSelectedLandmark(null);
  }, []);

  // Tab navigation
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    if (tab === 'globe') {
      setSelected(null);
      setLoadError(false);
      setLoadErrorMessage('');
      setZoomed(false);
      setZoomTarget(null);
      setSelectedCountryName(null);
      setSelectedLandmark(null);
    }
  }, []);

  // Discovery tracking
  const [discoveries, setDiscoveries] = useState(() => {
    try {
      const saved = window.localStorage.getItem(DISCOVERIES_KEY);
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [cardTap, setCardTap]         = useState(null);
  const [celebration, setCelebration] = useState(null);

  const handleCardTap = useCallback((type) => {
    if (!selectedCountryName) return;
    const isFirst = !discoveries.some(d => d.country === selectedCountryName && d.type === type);
    if (isFirst) {
      setDiscoveries(prev => [...prev, { country: selectedCountryName, type, ts: Date.now() }]);
      trackEvent('sticker_earned', { country: selectedCountryName, type });
    }
    const ts = Date.now();
    setCardTap({ type, ts });
    setCelebration({ type, ts, isFirst });
  }, [selectedCountryName, discoveries]);

  const handleAdventureReward = useCallback((type) => {
    if (!selectedCountryName) return;
    const rewardType = type === 'quiz' ? 'fact' : 'country';
    const isFirst = !discoveries.some(d => d.country === selectedCountryName && d.type === rewardType);
    if (isFirst) {
      setDiscoveries(prev => [...prev, { country: selectedCountryName, type: rewardType, ts: Date.now() }]);
      trackEvent('sticker_earned', { country: selectedCountryName, type: rewardType, source: type });
    }
    const ts = Date.now();
    setCardTap({ type: rewardType, ts });
    setCelebration({ type: rewardType, ts, isFirst });
  }, [selectedCountryName, discoveries]);

  useEffect(() => {
    try {
      window.localStorage.setItem(DISCOVERIES_KEY, JSON.stringify(discoveries));
    } catch {
      // Progress persistence is best-effort only.
    }
  }, [discoveries]);

  // Fire a quest_completed event the moment a quest's condition flips to done.
  const completedQuestIds = useRef(null);
  useEffect(() => {
    const doneIds = QUESTS.filter(q => q.check(discoveries)).map(q => q.id);
    if (completedQuestIds.current === null) {
      completedQuestIds.current = doneIds; // baseline on first render, don't fire
      return;
    }
    doneIds
      .filter(id => !completedQuestIds.current.includes(id))
      .forEach(id => trackEvent('quest_completed', { quest_id: id }));
    completedQuestIds.current = doneIds;
  }, [discoveries]);

  // Derived counts
  const stickerCount = discoveries.length;
  const questsDone   = QUESTS.filter(q => q.check(discoveries)).length;

  // Mascot
  const mascotState = loadError ? 'error' : loading ? 'loading' : selected ? 'country' : selectedLandmark ? 'landmark' : 'idle';

  const handleBearySuggest = useCallback(() => {
    if (allCountries.length === 0) return;
    const pick = allCountries[Math.floor(Math.random() * allCountries.length)];
    handleSearchSelect(pick);
  }, [allCountries, handleSearchSelect]);

  return (
    <GlobeErrorBoundary>
      <ConsentBanner />
      <div style={s.appShell} onPointerDown={warmUp}>
        <header style={{ ...s.siteHeader, ...(isMobile ? s.siteHeaderMobile : {}) }} aria-label="KahaniKids site links">
          <a href="/" style={s.brandLink} aria-label="KahaniKids World Explorer home">
            <span style={s.brandMark}>K</span>
            {!isMobile && <span style={s.brandText}>KahaniKids</span>}
          </a>
          <div style={s.headerRight}>
            <nav style={s.siteNav} aria-label="Main pages">
            <a href="/about.html" style={{ ...s.siteNavLink, ...(isMobile ? s.siteNavLinkMobile : {}) }}>About</a>
            <a href="/parents.html" style={{ ...s.siteNavLink, ...(isMobile ? s.siteNavLinkMobile : {}) }}>Parents</a>
            <a href="/teachers.html" style={{ ...s.siteNavLink, ...(isMobile ? s.siteNavLinkMobile : {}) }}>Teachers</a>
            <a href="/privacy.html" style={{ ...s.siteNavLink, ...(isMobile ? s.siteNavLinkMobile : {}) }}>Privacy</a>
          </nav>
            <SoundToggle />
          </div>
        </header>

        {activeTab === 'globe' && (
          <Globe
            onCountryClick={handleCountryClick}
            onLandmarkClick={handleLandmarkClick}
            zoomed={zoomed}
            zoomTarget={zoomTarget}
            selectedCountryName={selectedCountryName}
            searchTrigger={searchTrigger}
          />
        )}

        {/* GLOBE TAB */}
        {activeTab === 'globe' && (
          <>
            {!zoomed && !loading && !selectedLandmark && (
              <div style={{ ...s.searchWrap, ...(isMobile ? s.searchWrapMobile : {}) }} ref={searchRef}>
                <div style={s.searchInputRow}>
                  <svg style={s.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" />
                    <path d="m20 20-3.5-3.5" />
                  </svg>
                  <input
                    style={s.searchInput}
                    placeholder="Search a country"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Escape') setSearchQuery('');
                      if (e.key === 'Enter' && suggestions.length > 0) handleSearchSelect(suggestions[0]);
                    }}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {searchQuery && (
                    <button style={s.searchClear} onClick={() => setSearchQuery('')} aria-label="Clear search">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                        <path d="M6 6l12 12M18 6l-12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                {suggestions.length > 0 && (
                  <div style={s.searchDropdown}>
                    {suggestions.map(c => (
                      <button key={c.code} style={s.searchItem} onMouseDown={e => { e.preventDefault(); handleSearchSelect(c); }}>
                        <img
                          src={c.flagUrl || `/flags/4x3/${c.code.toLowerCase()}.svg`}
                          alt=""
                          style={s.searchFlag}
                          loading="lazy"
                        />
                        <span>{c.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {loading && (
              <div style={{ ...s.loadingWrap, ...(isMobile ? s.loadingWrapMobile : {}) }} role="status" aria-live="polite">
                <div style={{ ...s.loadingCard, ...(isMobile ? s.loadingCardMobile : {}) }}>
                  <div style={s.loadingEmoji}>🎨</div>
                  <p style={s.loadingTitle}>Painting your card...</p>
                  <p style={s.loadingHint}>Our friendly robot is drawing animals, food and a famous place.</p>
                  <div style={s.dots}>
                    <span style={{ ...s.dot, animationDelay: '0s' }} />
                    <span style={{ ...s.dot, animationDelay: '0.2s' }} />
                    <span style={{ ...s.dot, animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>
            )}

            {loadError && !loading && (
              <div style={{ ...s.loadingWrap, ...(isMobile ? s.loadingWrapMobile : {}) }} role="alert">
                <div style={{ ...s.loadingCard, ...(isMobile ? s.loadingCardMobile : {}) }}>
                  <div style={s.loadingEmoji}>🦒</div>
                  <p style={s.loadingTitle}>Hmm, that one's hiding.</p>
                  <p style={s.loadingHint}>{loadErrorMessage || getKidFriendlyError('country')}</p>
                  <button onClick={handleClose} style={s.errorBtnSmall}>Back to globe</button>
                </div>
              </div>
            )}

            <AnimatePresence>
              {selected && !loading && (
                <CountryAdventureModal
                  key="country-modal"
                  info={selected}
                  onClose={handleClose}
                  onCardTap={handleCardTap}
                  onReward={handleAdventureReward}
                />
              )}
            </AnimatePresence>

            <AnimatePresence>
              {selectedLandmark && !selected && (
                <LandmarkPanel key="landmark-panel" name={selectedLandmark} onClose={() => setSelectedLandmark(null)} />
              )}
            </AnimatePresence>

            {!selected && !loading && !loadError && !zoomed && (
              <div style={{ ...s.hintWrap, ...(isMobile ? s.hintWrapMobile : {}) }} aria-hidden="true">
                <span style={s.hintFinger}>✦</span>
                <span style={s.hintText}>Tap any country to explore</span>
              </div>
            )}
          </>
        )}

        {/* STICKERS TAB */}
        <AnimatePresence>
          {activeTab === 'stickers' && (
            <StickerBook key="sticker-book" discoveries={discoveries} onClose={() => handleTabChange('globe')} />
          )}
        </AnimatePresence>

        {/* QUESTS TAB */}
        <AnimatePresence>
          {activeTab === 'quests' && (
            <QuestList key="quest-list" discoveries={discoveries} onClose={() => handleTabChange('globe')} />
          )}
        </AnimatePresence>

        {/* Beary */}
        {activeTab === 'globe' && !selected && (
          <MascotGuide
            state={mascotState}
            countryName={selectedCountryName}
            cardTap={cardTap}
            onSuggest={handleBearySuggest}
          />
        )}

        {/* Confetti */}
        <DiscoveryEffect trigger={celebration} />

        {/* Bottom nav */}
        <BottomNav
          activeTab={activeTab}
          onTabChange={handleTabChange}
          stickerCount={stickerCount}
          questsDone={questsDone}
        />

      </div>
    </GlobeErrorBoundary>
  );
}

const s = {
  appShell: {
    position: 'relative',
    width: '100%',
    height: '100dvh',
    minHeight: '100vh',
    overflow: 'hidden',
  },
  siteHeader: {
    position: 'absolute',
    top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
    left: 18,
    right: 18,
    zIndex: 12,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    pointerEvents: 'none',
  },
  siteHeaderMobile: {
    top: 'calc(env(safe-area-inset-top, 0px) + 62px)',
    left: 12,
    right: 12,
    gap: 8,
  },
  brandLink: {
    pointerEvents: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 9,
    color: '#f8fafc',
    textDecoration: 'none',
    background: 'rgba(15, 27, 53, 0.48)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 999,
    padding: '8px 12px 8px 8px',
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    boxShadow: '0 12px 32px rgba(2, 6, 23, 0.26)',
  },
  brandMark: {
    display: 'grid',
    placeItems: 'center',
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #fbbf24 0%, #f97316 100%)',
    color: '#0f172a',
    fontSize: 14,
    fontWeight: 800,
  },
  brandText: {
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: 0,
  },
  siteNav: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    background: 'rgba(15, 27, 53, 0.48)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 999,
    padding: 4,
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    boxShadow: '0 12px 32px rgba(2, 6, 23, 0.26)',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    pointerEvents: 'none',
  },
  siteNavLink: {
    color: '#f8fafc',
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 700,
    padding: '7px 10px',
    borderRadius: 999,
    whiteSpace: 'nowrap',
  },
  siteNavLinkMobile: {
    fontSize: 11.5,
    padding: '6px 6px',
  },
  loadingWrap: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
    justifyContent: 'center', pointerEvents: 'none',
    padding: '20px 20px calc(env(safe-area-inset-bottom, 0px) + 110px)',
  },
  loadingCard: {
    pointerEvents: 'auto',
    background: 'rgba(8, 18, 38, 0.78)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid rgba(255,255,255,0.10)',
    color: '#f8fafc',
    borderRadius: 20,
    padding: '24px 28px 22px',
    maxWidth: 340,
    textAlign: 'center',
    boxShadow: '0 24px 60px rgba(2, 6, 23, 0.45)',
    animation: 'orboot-pop-in 320ms cubic-bezier(0.16, 1, 0.3, 1)',
  },
  loadingWrapMobile: {
    alignItems: 'flex-end',
    padding: '20px 16px calc(env(safe-area-inset-bottom, 0px) + 92px)',
  },
  loadingCardMobile: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 18,
    padding: '20px 18px 18px',
  },
  loadingEmoji: { fontSize: 48, marginBottom: 4, display: 'inline-block', animation: 'orboot-bob 1.8s ease-in-out infinite' },
  loadingTitle:  { fontSize: 18, fontWeight: 700, color: '#f8fafc', margin: '6px 0 4px', letterSpacing: '-0.01em' },
  loadingHint:   { fontSize: 13.5, color: '#94a3b8', margin: '4px 0 14px', lineHeight: 1.5 },
  dots:          { display: 'flex', justifyContent: 'center', gap: 6, marginTop: 4 },
  dot:           { width: 8, height: 8, borderRadius: '50%', background: '#fbbf24', display: 'inline-block', animation: 'orboot-bob 1s ease-in-out infinite' },
  errorBtnSmall: {
    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
    color: '#0f172a', border: 'none', borderRadius: 999, padding: '10px 20px',
    fontSize: 14, fontWeight: 700, boxShadow: '0 8px 24px rgba(251,191,36,0.32)',
    cursor: 'pointer', letterSpacing: '-0.005em',
  },
  searchWrap: {
    position: 'absolute',
    top: 'calc(env(safe-area-inset-top, 0px) + 18px)',
    left: '50%', transform: 'translateX(-50%)',
    width: 340, zIndex: 10,
  },
  searchWrapMobile: {
    top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
    width: 'calc(100% - 24px)',
    maxWidth: 440,
  },
  searchInputRow: {
    display: 'flex', alignItems: 'center',
    background: 'rgba(15, 27, 53, 0.55)',
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 999,
    padding: '10px 14px',
    boxShadow: '0 12px 32px rgba(2, 6, 23, 0.35)',
    gap: 10,
  },
  searchIcon:     { fontSize: 14, flexShrink: 0, opacity: 0.85, filter: 'grayscale(20%)' },
  searchInput:    { flex: 1, border: 'none', background: 'transparent', fontSize: 14, fontWeight: 500, color: '#f8fafc', minWidth: 0, letterSpacing: '-0.005em' },
  searchClear:    { background: 'rgba(255,255,255,0.08)', border: 'none', cursor: 'pointer', fontSize: 12, color: '#cbd5e1', padding: 0, width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0 },
  searchDropdown: {
    background: 'rgba(8, 18, 38, 0.92)',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 16, marginTop: 8,
    boxShadow: '0 24px 60px rgba(2, 6, 23, 0.5)',
    overflow: 'hidden',
  },
  searchItem:     { display: 'flex', alignItems: 'center', gap: 12, width: '100%', background: 'none', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '12px 16px', textAlign: 'left', fontSize: 14, fontWeight: 500, color: '#f8fafc', cursor: 'pointer', transition: 'background 0.15s var(--ease-out)' },
  searchFlag:     { width: 24, height: 18, borderRadius: 3, objectFit: 'cover', boxShadow: '0 0 0 1px rgba(255,255,255,0.08)', flexShrink: 0 },
  hintWrap: {
    position: 'absolute', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 110px)',
    left: '50%', transform: 'translateX(-50%)',
    display: 'inline-flex', alignItems: 'center', gap: 10,
    background: 'rgba(15, 27, 53, 0.55)',
    backdropFilter: 'blur(18px) saturate(160%)',
    WebkitBackdropFilter: 'blur(18px) saturate(160%)',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 999, padding: '10px 18px',
    boxShadow: '0 12px 32px rgba(2, 6, 23, 0.35)',
    pointerEvents: 'none', whiteSpace: 'nowrap',
    animation: 'orboot-fade-in 600ms ease-out',
  },
  hintWrapMobile: {
    left: 'auto',
    right: 12,
    transform: 'none',
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
    maxWidth: 'calc(100% - 116px)',
    padding: '9px 14px',
    gap: 8,
  },
  hintFinger: { fontSize: 18, display: 'inline-block', animation: 'orboot-bob 1.6s ease-in-out infinite' },
  hintText:   { fontSize: 13.5, fontWeight: 600, color: '#f8fafc', letterSpacing: '-0.005em' },
  errorScreen: {
    position: 'absolute', inset: 0,
    background: 'radial-gradient(ellipse at 50% 30%, #16315c 0%, #0b1d3a 40%, #050b1a 100%)',
    color: '#f8fafc',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 20, textAlign: 'center',
  },
  errorEmoji: { fontSize: 64, marginBottom: 12 },
  errorTitle:  { fontSize: 22, fontWeight: 700, color: '#f8fafc', margin: '4px 0', letterSpacing: '-0.01em' },
  errorBtn:    { background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', color: '#0f172a', border: 'none', borderRadius: 999, padding: '12px 26px', fontSize: 15, fontWeight: 700, boxShadow: '0 12px 32px rgba(251,191,36,0.35)', cursor: 'pointer', letterSpacing: '-0.005em' },
};
